import { Injectable } from '@angular/core';

import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { NgxImageCompressService } from 'ngx-image-compress';

import { ImageCompressionServicesModule } from './image-compression-services.module';

import { blobToFile, dataUrlToBlob, blobToBase64 } from '../utils/blob.utils';

export interface ImageOptions {
	height: number;
	width: number;
	quality: number;
}

@Injectable({
	providedIn: ImageCompressionServicesModule
})
export class ImageCompressionLogicService {

	constructor(
		private imageCompress: NgxImageCompressService
	) { }

	public compressImageToRatio(imageToCompress: File, options: ImageOptions): Observable<File> {
		return this.getImageResolution(imageToCompress).pipe(
			map((res: ImageOptions) => {
				return this.calculateRatio(res, options);
			}),
			switchMap(ratio => {
				return this.getBase64FromBlob(imageToCompress).pipe(
					switchMap(result => this.compressImage(result, ratio, options.quality)),
				);
			}),
			map(imageResult => {
				const pickedImage = blobToFile(dataUrlToBlob(imageResult));
				return pickedImage;
			})
		);
	}

	public compressImageToRatioBase64(imageToCompress: File, options: ImageOptions): Observable<string> {
		return this.getImageResolution(imageToCompress).pipe(
			map((res: ImageOptions) => {
				return this.calculateRatio(res, options);
			}),
			switchMap(ratio => {
				return this.getBase64FromBlob(imageToCompress).pipe(
					switchMap(result => this.compressImage(result, ratio, options.quality)),
				);
			})
		);
	}

	private getBase64FromBlob(imageToCompress: File) {
		return from(blobToBase64(imageToCompress));
	}

	private compressImage(imageResult, ratio, quality): Observable<string> {
		return from(this.imageCompress.compressFile(imageResult, '', ratio, quality));
	}

	private calculateRatio(res: ImageOptions, destRes: ImageOptions): number {
		let compressRatio = 100;
		if (res.height >= res.width) {
			if (res.height > destRes.height) {
				compressRatio = 100 * (destRes.height / res.height);
			}
		} else {
			if (res.width > destRes.width) {
				compressRatio = 100 * (destRes.width / res.width);
			}
		}
		return compressRatio;
	}

	private getImageResolution(image: File): Observable<any> {
		const file = image;
		const img = new Image();
		return new Observable(
			observer => {
				img.onload = () => {
					const resolution: ImageOptions = {
						width: img.width,
						height: img.height,
						quality: null
					};
					observer.next(resolution);
				};
				img.src = URL.createObjectURL(file);
			}
		);
	}
}


